import { extractCreatorId, getCreatorDisplayName } from '../admin/utils/authUtils';
import { normalizeProductPricesForDisplay } from './productPricing';

export const CREATOR_STOREFRONT_LS_PREFIX = 'sheenwear_creator_storefront_';

export function getCreatorStorefrontStorageKey(creatorId) {
  return `${CREATOR_STOREFRONT_LS_PREFIX}${String(creatorId || '').trim()}`;
}

export function readCreatorStorefrontFromStorage(creatorId) {
  if (typeof window === 'undefined' || !creatorId) return null;
  try {
    const raw = localStorage.getItem(getCreatorStorefrontStorageKey(creatorId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

export function writeCreatorStorefrontToStorage(creatorId, data) {
  if (typeof window === 'undefined' || !creatorId) return;
  try {
    localStorage.setItem(getCreatorStorefrontStorageKey(creatorId), JSON.stringify(data));
  } catch {
    /* ignore quota */
  }
}

export function resolveCreatorDisplayName(createdBy, fallback = 'Creator') {
  if (!createdBy) return fallback;
  if (typeof createdBy === 'object' && createdBy.role === 'admin') return 'Super Wear';
  const fromProfile = getCreatorDisplayName(createdBy);
  if (fromProfile) return fromProfile;
  if (typeof createdBy === 'object' && (createdBy.name || createdBy.email)) {
    return createdBy.name || createdBy.email;
  }
  return fallback;
}

function categoryIdFromField(field) {
  if (field == null || field === '') return '';
  if (typeof field === 'string') return field.trim();
  if (typeof field === 'object') {
    return String(field._id || field.id || field.slug || field.name || '').trim();
  }
  return String(field).trim();
}

function categoryLabelFromField(field, categoriesById, fallbackId) {
  if (typeof field === 'object' && field?.name) return field.name;
  const id = categoryIdFromField(field) || fallbackId;
  if (id && categoriesById.has(id)) return categoriesById.get(id).name || id;
  return id || 'Other';
}

/**
 * Group creator products under main category → subcategory sections.
 */
export function groupCreatorProductsByCategory(products, allCategories = []) {
  const categoriesById = new Map();
  (allCategories || []).forEach((c) => {
    const id = c._id || c.id;
    if (id) categoriesById.set(String(id), c);
  });

  const groups = new Map();

  (products || []).forEach((product) => {
    const mainId = categoryIdFromField(product.category) || 'other';
    const subId = categoryIdFromField(product.subcategory) || '';
    const key = `${mainId}::${subId}`;

    if (!groups.has(key)) {
      groups.set(key, {
        key,
        mainCategoryId: mainId,
        subcategoryId: subId,
        mainCategoryName: categoryLabelFromField(product.category, categoriesById, mainId),
        subcategoryName: subId
          ? categoryLabelFromField(product.subcategory, categoriesById, subId)
          : '',
        products: [],
      });
    }
    groups.get(key).products.push(product);
  });

  return Array.from(groups.values()).sort((a, b) => {
    const main = a.mainCategoryName.localeCompare(b.mainCategoryName);
    if (main !== 0) return main;
    return a.subcategoryName.localeCompare(b.subcategoryName);
  });
}

export function normalizeCreatorProductList(list) {
  if (!Array.isArray(list)) return [];
  return list.map((p) => {
    const prices = normalizeProductPricesForDisplay(p);
    return {
      ...p,
      id: p.id || p._id,
      _id: p._id || p.id,
      price: prices.price,
      originalPrice: prices.originalPrice,
      salePrice: prices.salePrice,
      isOnSale: prices.isOnSale,
    };
  });
}

export function productMatchesCreator(product, creatorId) {
  if (!product || !creatorId) return false;
  return extractCreatorId(product.createdBy) === String(creatorId);
}
